import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { ChevronDown, Clock, Search } from 'lucide-react';

/**
 * SymbolComboBox — autocomplete/typeahead for tradeable asset symbols.
 *
 * Props:
 *   value        – current symbol string
 *   onChange      – (symbol: string) => void
 *   assets       – full asset list (cached array)
 *   recentAssets – user's recent assets (array, sorted by last_used_at desc)
 *   placeholder  – input placeholder
 *   className    – extra classes for the wrapper input
 *   required     – html required attr
 */
const SymbolComboBox = ({
  value = '',
  onChange,
  assets = [],
  recentAssets = [],
  placeholder = 'Search symbol...',
  className,
  required,
  id,
}) => {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build a set of recent symbols for quick lookup
  const recentSymbolSet = useMemo(
    () => new Set(recentAssets.map((a) => a.symbol)),
    [recentAssets]
  );

  // Filtered results based on query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) {
      // No query — show recents first, then first batch of all assets
      const recents = recentAssets.slice(0, 10);
      const rest = assets.filter((a) => !recentSymbolSet.has(a.symbol)).slice(0, 40);
      return { recents, results: rest };
    }

    // Filter assets matching query on symbol or name
    const matches = assets.filter((a) => {
      const sym = a.symbol.toLowerCase();
      const name = (a.name || '').toLowerCase();
      return sym.includes(q) || name.includes(q);
    });

    // Split into recents and others
    const recents = matches.filter((a) => recentSymbolSet.has(a.symbol)).slice(0, 5);
    const others = matches.filter((a) => !recentSymbolSet.has(a.symbol)).slice(0, 45);

    return { recents, results: others };
  }, [query, assets, recentAssets, recentSymbolSet]);

  const allItems = useMemo(() => {
    return [...filtered.recents, ...filtered.results];
  }, [filtered]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIndex];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const selectItem = useCallback(
    (asset) => {
      setQuery(asset.symbol);
      onChange(asset.symbol);
      setIsOpen(false);
      setHighlightIndex(-1);
    },
    [onChange]
  );

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setIsOpen(true);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, allItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        if (highlightIndex >= 0 && highlightIndex < allItems.length) {
          e.preventDefault();
          selectItem(allItems[highlightIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        break;
      default:
        break;
    }
  };

  const renderItem = (asset, index, isRecent) => {
    const isHighlighted = index === highlightIndex;
    return (
      <li
        key={`${asset.symbol}-${index}`}
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer text-sm',
          isHighlighted
            ? 'bg-accent text-accent-foreground'
            : 'hover:bg-accent/50'
        )}
        onMouseEnter={() => setHighlightIndex(index)}
        onMouseDown={(e) => {
          e.preventDefault(); // prevent blur before click registers
          selectItem(asset);
        }}
      >
        {isRecent && <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
        <span className="font-medium">{asset.symbol}</span>
        {asset.name && (
          <span className="text-muted-foreground truncate text-xs">
            {asset.name}
          </span>
        )}
        {asset.category && (
          <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
            {asset.category}
          </span>
        )}
      </li>
    );
  };

  // compute item indices across both sections
  let runningIndex = 0;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background pl-8 pr-8 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          {filtered.recents.length > 0 && (
            <>
              <li className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                Recent
              </li>
              {filtered.recents.map((asset) => {
                const idx = runningIndex++;
                return renderItem(asset, idx, true);
              })}
            </>
          )}

          {filtered.results.length > 0 && (
            <>
              <li className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                {query.trim() ? 'Results' : 'All Assets'}
              </li>
              {filtered.results.map((asset) => {
                const idx = runningIndex++;
                return renderItem(asset, idx, false);
              })}
            </>
          )}

          {filtered.recents.length === 0 && filtered.results.length === 0 && (
            <li className="px-3 py-4 text-sm text-center text-muted-foreground">
              No matching symbols found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export { SymbolComboBox };
